export type ConfirmationSurface = 'copilot' | 'mcp';
export type PreferenceMode = 'auto_when_allowed' | 'require_confirmation';
export type PreferenceSelectorType = 'tool' | 'category' | 'capability';

export interface AssistantPreference {
  selectorType?: PreferenceSelectorType;
  selector?: string;
  /** Legacy rows returned by older backends. */
  capability?: string;
  mode: PreferenceMode;
  surface: string;
}

export interface ConfirmationToolPreferenceTarget {
  name: string;
  category: string;
  actionCapability?: string;
  confirmationDefault: PreferenceMode;
}

export function selectorsForToolGroup(
  category: string,
  toolNames: string[]
): Array<{ type: 'category' | 'tool'; value: string }> {
  return [
    { type: 'category', value: category },
    ...toolNames.map(value => ({ type: 'tool' as const, value })),
  ];
}

function preferenceSelector(preference: AssistantPreference) {
  return {
    type: preference.selectorType ?? 'capability',
    value: preference.selector ?? preference.capability ?? '',
  };
}

export function resolveToolPreferenceMode(
  preferences: AssistantPreference[],
  surface: ConfirmationSurface,
  tool: ConfirmationToolPreferenceTarget
): PreferenceMode {
  const selectors: Array<[PreferenceSelectorType, string | undefined]> = [
    ['tool', tool.name],
    ['category', tool.category],
    ['capability', tool.actionCapability],
  ];
  for (const candidateSurface of [surface, 'all']) {
    for (const [type, value] of selectors) {
      if (!value) continue;
      const match = preferences.find(preference => {
        const selector = preferenceSelector(preference);
        return (
          preference.surface === candidateSurface &&
          selector.type === type &&
          selector.value === value
        );
      });
      if (match) return match.mode;
    }
  }
  // Connected MCP apps own confirmation UX by default. Explicit user
  // preferences still win above, while mandatory workspace policy remains a
  // backend ceiling and cannot be weakened here.
  return surface === 'mcp' ? 'auto_when_allowed' : tool.confirmationDefault;
}

export function isToolDelegated(
  preferences: AssistantPreference[],
  surface: ConfirmationSurface,
  tool: ConfirmationToolPreferenceTarget
): boolean {
  return (
    resolveToolPreferenceMode(preferences, surface, tool) ===
    'auto_when_allowed'
  );
}

export function replaceSurfacePreference(
  preferences: AssistantPreference[],
  surface: ConfirmationSurface,
  selectorType: PreferenceSelectorType,
  selector: string,
  mode: PreferenceMode
): AssistantPreference[] {
  return [
    ...preferences.filter(preference => {
      const current = preferenceSelector(preference);
      return !(
        preference.surface === surface &&
        current.type === selectorType &&
        current.value === selector
      );
    }),
    { surface, selectorType, selector, mode },
  ];
}

// Compatibility helpers for older focused tests and clients.
export function resolvePreferenceMode(
  preferences: AssistantPreference[],
  surface: ConfirmationSurface,
  capability: string
): PreferenceMode {
  return resolveToolPreferenceMode(preferences, surface, {
    name: '',
    category: '',
    actionCapability: capability,
    confirmationDefault: 'require_confirmation',
  });
}

export function isAutoApproved(
  preferences: AssistantPreference[],
  surface: ConfirmationSurface,
  capability: string | undefined
): boolean {
  return (
    !!capability &&
    resolvePreferenceMode(preferences, surface, capability) ===
      'auto_when_allowed'
  );
}

export function fulfilledCapabilities(
  capabilities: string[],
  results: PromiseSettledResult<void>[]
): string[] {
  return capabilities.filter(
    (_, index) => results[index]?.status === 'fulfilled'
  );
}
